import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, LineElement,
  PointElement, Title, Tooltip, Legend, Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { api } from '../api';
import { formatPts, formatDate, formatBattleName, formatDateTime, formatSeconds, isLive } from '../utils';

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Title, Tooltip, Legend, Filler);

const GRID_COLOR = '#30363d';
const TICK_COLOR = '#7d8590';
const sharedScales = {
  x: { ticks: { color: TICK_COLOR, maxRotation: 45, font: { size: 10 } }, grid: { color: GRID_COLOR } },
  y: { ticks: { color: TICK_COLOR, callback: v => formatPts(v) }, grid: { color: GRID_COLOR } },
};

export default function PlayerProfile() {
  const { robloxId } = useParams();
  const [searchParams] = useSearchParams();
  const requestedBattleId = searchParams.get('battleId');
  const navigate = useNavigate();

  const [player, setPlayer]      = useState(null);
  const [history, setHistory]    = useState([]);
  const [disconnects, setDiscs]  = useState([]);
  const [avatar, setAvatar]      = useState(null);
  const [bStats, setBStats]      = useState(null);
  const [timeline, setTimeline]  = useState(null);
  const [selBattle, setSelBattle]= useState(null);
  const [loadingBS, setLoadingBS]= useState(false);
  const [error, setError]        = useState(null);
  const [loading, setLoading]    = useState(true);

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.player(robloxId),
      api.thumbnails(robloxId).catch(() => ({ data: [] })),
    ]).then(([data, thumb]) => {
      setPlayer(data.player);
      setHistory(data.history);
      setDiscs(data.disconnects || []);
      const img = thumb.data?.find(t => t.state === 'Completed')?.imageUrl;
      if (img) setAvatar(img);
      setLoading(false);

      // Default: the battle the user came from, else the current live one, else the latest
      if (data.history.length) {
        const wanted = requestedBattleId && data.history.find(h => String(h.battleId) === requestedBattleId);
        const sortedByDate = [...data.history].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        const live = sortedByDate.find(h => isLive(h));
        setSelBattle((wanted || live || sortedByDate[0]).battleId);
      }
    }).catch(e => { setError(e.message); setLoading(false); });
  }, [robloxId, requestedBattleId]);

  useEffect(() => {
    if (!selBattle) return;
    setLoadingBS(true);
    setTimeline(null);
    api.playerStats(robloxId, selBattle)
      .then(s => { setBStats(s); setLoadingBS(false); })
      .catch(() => { setBStats(null); setLoadingBS(false); });
    api.playerTimeline(robloxId, selBattle)
      .then(setTimeline)
      .catch(() => setTimeline([]));
  }, [selBattle, robloxId]);

  if (loading) return <div className="page-center"><div className="spinner" /></div>;
  if (error)   return <div className="page"><div className="error-box">{error}</div></div>;
  if (!player) return <div className="page"><div className="empty-msg">Player not found.</div></div>;

  const selHistory = history.find(h => h.battleId === selBattle);
  const selLive    = isLive(selHistory);

  const finishedHistory = history
    .filter(h => h.finishPosition != null)
    .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  const positionChart = finishedHistory.length > 0 ? {
    labels: finishedHistory.map(h => formatBattleName(h.battleName)),
    datasets: [{
      label: 'Finishing position',
      data: finishedHistory.map(h => h.finishPosition),
      borderColor: '#8b5cf6',
      backgroundColor: 'rgba(139, 92, 246, .13)',
      borderWidth: 3,
      pointRadius: 4,
      pointHoverRadius: 6,
      pointBackgroundColor: '#8b5cf6',
      fill: true,
      tension: .25,
    }],
  } : null;
  const positionOpts = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: ctx => {
        const h = finishedHistory[ctx.dataIndex];
        return ` Finished #${ctx.raw} of ${h.fieldSize}`;
      }}},
    },
    scales: {
      x: { ticks: { color: TICK_COLOR, maxRotation: 35, font: { size: 10 } }, grid: { display: false } },
      y: {
        reverse: true,
        beginAtZero: false,
        suggestedMin: 1,
        ticks: { color: TICK_COLOR, precision: 0, callback: v => `#${v}` },
        grid: { color: GRID_COLOR },
        title: { display: true, text: 'Finishing position (higher is better)', color: TICK_COLOR },
      },
    },
  };

  const timelineChart = timeline && timeline.length > 1 ? {
    labels: timeline.map(p => formatDateTime(p.time)),
    datasets: [{
      label: 'Points',
      data: timeline.map(p => p.points),
      borderColor: '#58a6ff',
      backgroundColor: 'rgba(88,166,255,0.12)',
      borderWidth: 2,
      pointRadius: 0,
      pointHoverRadius: 4,
      fill: true,
      tension: 0.25,
      // ponytail: no-gain = identical points on the two adjacent snapshots; matches the
      // stall definition already used server-side, just without its 10min-gap threshold.
      segment: { borderColor: ctx => ctx.p0.parsed.y === ctx.p1.parsed.y ? '#f85149' : undefined },
    }],
  } : null;

  const chartOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: { label: ctx => ` ${formatPts(ctx.raw)}` } },
    },
    scales: {
      x: { ...sharedScales.x, ticks: { ...sharedScales.x.ticks, maxTicksLimit: 8 } },
      y: sharedScales.y,
    },
  };

  // Recent gain rate: compare the latest snapshot against one from ~1h ago
  // (or the oldest available, for battles younger than an hour).
  let pointsPerHour = null;
  if (timeline && timeline.length >= 2) {
    const last = timeline[timeline.length - 1];
    const lastMs = new Date(last.time).getTime();
    const target = lastMs - 3_600_000;
    const base = timeline.find(p => new Date(p.time).getTime() >= target) ?? timeline[0];
    const hours = (lastMs - new Date(base.time).getTime()) / 3_600_000;
    if (hours > 0 && base !== last) pointsPerHour = Math.round((last.points - base.points) / hours);
  }

  const vsAvg    = bStats?.clanAvgPoints > 0
    ? ((Number(bStats.maxPoints) - bStats.clanAvgPoints) / bStats.clanAvgPoints * 100).toFixed(0)
    : null;
  const missedPct = bStats ? Math.round(bStats.missedRounds / (bStats.totalRounds || 1) * 100) : 0;
  const contribution = bStats?.clanTotalPoints > 0
    ? (bStats.maxPoints / bStats.clanTotalPoints * 100).toFixed(1)
    : null;
  // Hourly-tracked (auto, unlinked) clans sample far too coarsely for
  // round-attendance or stall detection to mean anything.
  const hourly = bStats?.tracking === 'hourly';

  return (
    <div className="page fade-in">
      <button className="btn" style={{ marginBottom: 16 }} onClick={() => navigate(-1)}>← Back</button>

      {/* Profile card */}
      <div className="pp-card">
        <div className="pp-header">
          {avatar
            ? <img className="pp-avatar" src={avatar} alt={player.cached_username} />
            : <div className="pp-avatar-ph">{player.cached_username?.[0]?.toUpperCase()}</div>}
          <div>
            <div className="pp-name">{player.cached_username}</div>
            <div className="pp-meta">
              {player.clanName && <span className="pp-clan">{player.clanName}</span>}
              <span style={{ color: 'var(--muted)', fontSize: '.85rem' }}>
                {history.length} battle{history.length === 1 ? '' : 's'} tracked
              </span>
              <a
                className="pp-roblox-link"
                href={`https://www.roblox.com/users/${player.robloxId}/profile`}
                target="_blank" rel="noopener noreferrer"
              >
                Roblox ↗
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Cross-battle position is based on every historical score row. */}
      {positionChart && (
        <div className="chart-card performance-chart mb-20">
          <div className="chart-heading-row">
            <div>
              <div className="chart-title">Finishing position by battle</div>
              <div className="chart-subtitle">Removed players remain included in each battle's field.</div>
            </div>
            <span className="badge badge-purple">{finishedHistory.length} battles</span>
          </div>
          <div className="chart-wrap" style={{ height: 320 }}>
            <Line data={positionChart} options={positionOpts} />
          </div>
        </div>
      )}

      {/* Per-battle stats */}
      {history.length > 0 && (
        <div className="pbs-section">
          <div className="pbs-header">
            <div className="pbs-title">Battle Performance</div>
            <select
              value={selBattle || ''}
              onChange={e => setSelBattle(Number(e.target.value))}
            >
              {[...history].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map(h => (
                <option key={h.battleId} value={h.battleId}>{formatBattleName(h.battleName)}</option>
              ))}
            </select>
          </div>

          {loadingBS && <div className="page-center" style={{ padding: 20 }}><div className="spinner spinner-sm" /></div>}

          {!loadingBS && bStats && bStats.participated && (
            <>
              <div className="pbs-cards">
                <div className="pbs-card">
                  <div className="pbs-val text-blue">{formatPts(bStats.maxPoints)}</div>
                  <div className="pbs-lbl">Points Earned</div>
                </div>
                <div className="pbs-card">
                  <div className="pbs-val text-green">+{formatPts(bStats.gainedPoints)}</div>
                  <div className="pbs-lbl">Gained This Battle</div>
                </div>
                {pointsPerHour !== null && (
                  <div className="pbs-card">
                    <div className="pbs-val text-blue">{pointsPerHour >= 0 ? '+' : ''}{formatPts(pointsPerHour)}</div>
                    <div className="pbs-lbl">Pts / Hour{selLive ? '' : ' (recent)'}</div>
                  </div>
                )}
                <div className="pbs-card">
                  <div className="pbs-val text-amber">#{bStats.rankInClan} <span style={{ fontSize: '.8rem', color: 'var(--muted)' }}>of {bStats.clanSize}</span></div>
                  <div className="pbs-lbl">Clan Rank</div>
                </div>
                {!!bStats.rankChange && (
                  <div className="pbs-card">
                    <div className={`pbs-val ${bStats.rankChange > 0 ? 'text-green' : 'text-red'}`}>
                      {bStats.rankChange > 0 ? '↑' : '↓'} {Math.abs(bStats.rankChange)}
                    </div>
                    <div className="pbs-lbl">Positions {bStats.rankChange > 0 ? 'Gained' : 'Lost'} (24h, was #{bStats.rank24hAgo})</div>
                  </div>
                )}
                <div className="pbs-card">
                  <div className={`pbs-val ${vsAvg === null ? '' : Number(vsAvg) >= 0 ? 'text-green' : 'text-red'}`}>
                    {vsAvg === null ? '—' : Number(vsAvg) >= 0 ? `+${vsAvg}%` : `${vsAvg}%`}
                  </div>
                  <div className="pbs-lbl">vs Clan Avg ({formatPts(bStats.clanAvgPoints)})</div>
                </div>
                {contribution !== null && (
                  <div className="pbs-card">
                    <div className="pbs-val text-blue">{contribution}%</div>
                    <div className="pbs-lbl">Share of Clan Points</div>
                  </div>
                )}
                {!hourly && (
                  <div className="pbs-card">
                    <div className="pbs-val">{bStats.playerRounds}<span style={{ fontSize: '.8rem', color: 'var(--muted)' }}> / {bStats.totalRounds}</span></div>
                    <div className="pbs-lbl">Rounds Present</div>
                  </div>
                )}
                {!hourly && (
                  <div className="pbs-card">
                    <div className={`pbs-val ${bStats.missedRounds > 0 ? 'text-red' : 'text-green'}`}>{bStats.missedRounds}</div>
                    <div className="pbs-lbl">Missed Rounds ({missedPct}%)</div>
                  </div>
                )}
                {!hourly && (
                  <div className="pbs-card">
                    <div className={`pbs-val ${bStats.totalDisconnectedSeconds > 0 ? 'text-red' : 'text-green'}`}>
                      {bStats.totalDisconnectedSeconds > 0 ? formatSeconds(bStats.totalDisconnectedSeconds) : '0m'}
                    </div>
                    <div className="pbs-lbl">
                      Disconnected{bStats.stallCount > 0 ? ` (${bStats.stallCount} stall${bStats.stallCount === 1 ? '' : 's'})` : ''}
                    </div>
                  </div>
                )}
                {!hourly && (
                  <div className="pbs-card">
                    <div className="pbs-val">
                      {bStats.currentlyStalled
                        ? <span className="badge" style={{ background: 'var(--red-dim)', color: 'var(--red)' }}>🔴 Disconnected</span>
                        : <span className="badge badge-done">Active</span>}
                    </div>
                    <div className="pbs-lbl">Current Status</div>
                  </div>
                )}
              </div>
              {hourly && (
                <div className="empty-msg" style={{ marginTop: 12, padding: '10px 0' }}>
                  ⏱ This clan is auto-tracked hourly — round attendance and disconnect stats aren't tracked at that resolution.
                </div>
              )}
            </>
          )}
          {!loadingBS && bStats && !bStats.participated && (
            <div className="empty-msg" style={{ padding: '16px 0' }}>No data for this battle.</div>
          )}
        </div>
      )}

      {/* Points over time — selected battle (defaults to the current live one) */}
      {selBattle && (
        <div className="chart-card mb-20">
          <div className="chart-title">
            Points Over Time — {selHistory ? formatBattleName(selHistory.battleName) : '…'}
            {selLive && <span className="badge badge-live" style={{ marginLeft: 10 }}>Live</span>}
          </div>
          {!timeline && <div className="page-center" style={{ padding: 20 }}><div className="spinner spinner-sm" /></div>}
          {timeline && !timelineChart && (
            <div className="empty-msg" style={{ padding: '12px 0' }}>Not enough data yet for a graph.</div>
          )}
          {timelineChart && (
            <>
              <div className="chart-wrap" style={{ height: 300 }}>
                <Line data={timelineChart} options={chartOpts} />
              </div>
              <div style={{ color: 'var(--muted)', fontSize: '.8rem', marginTop: 6 }}>
                <span style={{ color: '#f85149' }}>■</span> no gain between snapshots (possible disconnect)
              </div>
            </>
          )}
        </div>
      )}

      {/* Disconnect periods for the selected battle */}
      {!hourly && selHistory && (() => {
        const battleDiscs = disconnects.filter(d => d.battleName === selHistory.battleName);
        return battleDiscs.length > 0 && (
          <div className="mb-20">
            <div className="section-title mb-12">Disconnect Periods — {formatBattleName(selHistory.battleName)}</div>
            <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Start</th><th>Duration</th><th>Status</th></tr>
              </thead>
              <tbody>
                {battleDiscs.map((d, i) => (
                  <tr key={i}>
                    <td style={{ color: 'var(--muted)', fontSize: '.83rem' }}>{formatDateTime(d.startTime)}</td>
                    <td style={{ fontSize: '.85rem' }}>{d.durationMinutes != null ? `${d.durationMinutes}m` : '—'}</td>
                    <td>
                      {d.endTime
                        ? <span className="badge badge-done">Resolved</span>
                        : <span className="badge badge-live">Active</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        );
      })()}

      {/* History table */}
      <div className="section-title mb-12">Battle History</div>
      {history.length === 0 && <div className="empty-msg">No battle history.</div>}
      {history.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Battle</th>
                <th>Date</th>
                <th>Clan</th>
                <th>Points</th>
                <th>Finish</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {[...history].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map(h => {
                const live = !h.finished && Number(h.finishesAt) * 1000 > Date.now();
                const pts  = Number(h.points);
                const maxP = Math.max(...history.map(hh => Number(hh.points)));
                const pct  = maxP > 0 ? (pts / maxP * 100).toFixed(1) : 0;
                return (
                  <tr key={h.battleId}>
                    <td>
                      <button
                        style={{ background: 'none', border: 'none', color: 'var(--blue)', cursor: 'pointer', fontWeight: 600, fontSize: '.88rem' }}
                        onClick={() => navigate(`/battle/${h.battleId}`)}
                      >
                        {formatBattleName(h.battleName)}
                      </button>
                    </td>
                    <td style={{ color: 'var(--muted)', fontSize: '.83rem' }}>{formatDate(h.createdAt)}</td>
                    <td style={{ fontSize: '.85rem' }}>{h.clanName}</td>
                    <td>
                      <div className="hist-pts-cell">
                        <div className="bar-track"><div className="bar-fill" style={{ width: `${pct}%` }} /></div>
                        <span className="hist-pts">{formatPts(pts)}</span>
                      </div>
                    </td>
                    <td><span className="finish-pill">#{h.finishPosition} <small>of {h.fieldSize}</small></span></td>
                    <td>
                      {live
                        ? <span className="badge badge-live">Live</span>
                        : <span className="badge badge-done">Done</span>}
                    </td>
                    <td>
                      {h.clanId && (
                        <button
                          style={{ background: 'none', border: 'none', color: 'var(--blue)', cursor: 'pointer', fontSize: '.8rem', padding: 0, whiteSpace: 'nowrap' }}
                          onClick={() => navigate(`/clan-stats?clanId=${h.clanId}&battleId=${h.battleId}`)}
                        >
                          Clan Stats →
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Disconnects */}
      {disconnects.length > 0 && (
        <>
          <div className="section-title mb-12" style={{ marginTop: 24 }}>Disconnect History</div>
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Battle</th><th>Start</th><th>Duration</th><th>Status</th></tr>
              </thead>
              <tbody>
                {disconnects.map((d, i) => (
                  <tr key={i}>
                    <td style={{ fontSize: '.85rem' }}>{formatBattleName(d.battleName)}</td>
                    <td style={{ color: 'var(--muted)', fontSize: '.83rem' }}>{formatDateTime(d.startTime)}</td>
                    <td style={{ fontSize: '.85rem' }}>{d.durationMinutes != null ? `${d.durationMinutes}m` : '—'}</td>
                    <td>
                      {d.endTime
                        ? <span className="badge badge-done">Resolved</span>
                        : <span className="badge badge-live">Active</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
